import {
  COLORES_ESTADO_PLANES,
  LabelEstadoPlan,
  resolverEtiquetaCanonicaEstadoPlan
} from './colores-visitas.config';

/** Item del gráfico «Estado General de Planes» en dashboard. */
export interface EstadoPlanDashboardItem {
  label: string;
  value: number;
}

/** Series del gráfico circular (orden ids 1–3: Diagnóstico → Diseño → Ejecución). */
export const SERIES_ESTADO_PLANES_DASHBOARD: { label: LabelEstadoPlan; color: string }[] = [
  { label: 'Diagnóstico', color: COLORES_ESTADO_PLANES['Diagnóstico'].hex },
  { label: 'Diseño', color: COLORES_ESTADO_PLANES['Diseño'].hex },
  { label: 'Ejecución', color: COLORES_ESTADO_PLANES['Ejecución'].hex }
];

function acumularConteoEstadoPlan(
  acum: Record<LabelEstadoPlan, number>,
  etiquetaRaw: string,
  valor: unknown
): void {
  const canon = resolverEtiquetaCanonicaEstadoPlan(etiquetaRaw);
  if (!canon) return;
  acum[canon] = (acum[canon] ?? 0) + (Number(valor) || 0);
}

/**
 * Normaliza la respuesta de GET estadoPlanes (array u objeto) al modelo del gráfico.
 * Devuelve 3 ítems en orden fijo; si la suma es 0, devuelve array vacío.
 */
export function normalizarEstadoPlanesValor(valor: unknown): EstadoPlanDashboardItem[] {
  const acum: Record<LabelEstadoPlan, number> = {
    'Diagnóstico': 0,
    'Diseño': 0,
    'Ejecución': 0,
    'Cerrado': 0 // Se mantiene en el acumulador por si viene de la API, pero no se grafica porque la serie no lo incluye
  };

  if (Array.isArray(valor)) {
    for (const item of valor) {
      if (!item || typeof item !== 'object') continue;
      const row = item as Record<string, unknown>;
      const etiqueta = String(row['label'] ?? row['etiqueta'] ?? row['estado'] ?? '');
      const cantidad = row['value'] ?? row['valor'] ?? row['cantidad'];
      if (etiqueta) {
        acumularConteoEstadoPlan(acum, etiqueta, cantidad);
      }
    }
  } else if (valor !== null && typeof valor === 'object') {
    for (const [clave, cantidad] of Object.entries(valor as Record<string, unknown>)) {
      acumularConteoEstadoPlan(acum, clave, cantidad);
    }
  }

  const items = SERIES_ESTADO_PLANES_DASHBOARD.map(serie => ({
    label: serie.label,
    value: acum[serie.label] ?? 0
  }));

  const total = items.reduce((suma, item) => suma + item.value, 0);
  return total > 0 ? items : [];
}

/**
 * Configuración centralizada para Estados de Plan de Negocio (clases CSS, etiquetas, helpers).
 *
 * Los hex/rgb de cada estado se toman de **COLORES_ESTADO_PLANES** (tarjeta Estado General de Planes en /dashboard).
 *
 * La API GET /api/v1/negocio/perfil/{id}/jerarquia/{id} devuelve
 * plan_negocio con `estado` texto y/o `id_estado_plan_negocio` (1 Diagnóstico, 2 Diseño, 3 Ejecución).
 * Si ambos faltan o `id_estado_plan_negocio` es null, se muestra como **Diagnóstico** (mismo color que id 1).
 */

/** Claves normalizadas para clases CSS (uso interno) */
export type ClaveEstadoPlanNegocio =
  | 'diagnostico'
  | 'diseno'
  | 'ejecucion'
  | 'cerrado'
  | 'default';

/** Etiquetas que devuelve la API (plan_negocio.estado) */
export type EtiquetaEstadoPlanAPI = 'Diagnóstico' | 'Diseño' | 'Ejecución' | 'Cerrado';

const ID_PN_DIAGNOSTICO = 1;
const ID_PN_DISENO = 2;
const ID_PN_EJECUCION = 3;
const ID_PN_CERRADO = 4;

/**
 * Valor crudo para etiqueta/clase: prioriza `id_estado_plan_negocio` (1–3), luego `estado` texto;
 * sin id ni estado → null (se normaliza a Diagnóstico en UI).
 */
export function estadoPlanNegocioRawParaUi(
  plan: {
    estado?: string | null;
    id_estado_plan_negocio?: number | string | null;
  } | null | undefined
): number | string | null {
  if (!plan) return null;
  const id = plan.id_estado_plan_negocio;
  if (id !== null && id !== undefined && String(id).trim() !== '') {
    const n = Number(id);
    if (!Number.isNaN(n)) return n;
  }
  return plan.estado ?? null;
}

/**
 * Estados de plan de negocio. Mismos nombres y colores que "Estado General de Planes".
 */
export const ESTADOS_PLAN_NEGOCIO = {
  Diagnóstico: { etiqueta: 'Diagnóstico', clave: 'diagnostico' as const },
  Diseño: { etiqueta: 'Diseño', clave: 'diseno' as const },
  Ejecución: { etiqueta: 'Ejecución', clave: 'ejecucion' as const },
  Cerrado: { etiqueta: 'Cerrado', clave: 'cerrado' as const },
  default: { etiqueta: 'Sin clasificar', clave: 'default' as const }
} as const;

/** Lista para iterar (ej. páginas de demo UI). */
export const ESTADOS_PLAN_NEGOCIO_LISTA: readonly { clave: ClaveEstadoPlanNegocio; etiqueta: string }[] = [
  { clave: 'diagnostico', etiqueta: 'Diagnóstico' },
  { clave: 'diseno', etiqueta: 'Diseño' },
  { clave: 'ejecucion', etiqueta: 'Ejecución' },
  { clave: 'cerrado', etiqueta: 'Cerrado' },
  { clave: 'default', etiqueta: 'Default' }
];

/**
 * Colores por estado (alias de COLORES_ESTADO_PLANES para claves internas diagnostico | diseno | …).
 */
export const COLORES_ESTADOS_PLAN_NEGOCIO = {
  diagnostico: COLORES_ESTADO_PLANES['Diagnóstico'],
  diseno: COLORES_ESTADO_PLANES['Diseño'],
  ejecucion: COLORES_ESTADO_PLANES['Ejecución'],
  cerrado: COLORES_ESTADO_PLANES['Cerrado'],
  default: COLORES_ESTADO_PLANES.default
} as const;

/**
 * Mapeo de clases CSS a claves de color.
 * `estado-pn-sin-plan` se mantiene por compatibilidad y usa el mismo color que Diagnóstico.
 */
export const MAPEO_CLASES_ESTADOS_PLAN_NEGOCIO: {
  [key: string]: keyof typeof COLORES_ESTADOS_PLAN_NEGOCIO;
} = {
  'estado-pn-sin-plan': 'diagnostico',
  'estado-pn-diagnostico': 'diagnostico',
  'estado-pn-diseno': 'diseno',
  'estado-pn-ejecucion': 'ejecucion',
  'estado-pn-implementacion': 'ejecucion',
  'estado-pn-cerrado': 'cerrado',
  'estado-pn-default': 'default'
};

const ETIQUETAS_API_VALIDAS: EtiquetaEstadoPlanAPI[] = ['Diagnóstico', 'Diseño', 'Ejecución', 'Cerrado'];

function normalizarDesdeNumero(n: number): { clave: ClaveEstadoPlanNegocio; etiqueta: string } {
  if (n === ID_PN_DIAGNOSTICO) {
    return { clave: 'diagnostico', etiqueta: 'Diagnóstico' };
  }
  if (n === ID_PN_DISENO) {
    return { clave: 'diseno', etiqueta: 'Diseño' };
  }
  if (n === ID_PN_EJECUCION) {
    return { clave: 'ejecucion', etiqueta: 'Ejecución' };
  }
  if (n === ID_PN_CERRADO) {
    return { clave: 'cerrado', etiqueta: 'Cerrado' };
  }
  return { clave: 'default', etiqueta: 'Sin clasificar' };
}

function normalizarEstado(
  estado: number | string | null | undefined
): { clave: ClaveEstadoPlanNegocio; etiqueta: string } {
  if (estado === null || estado === undefined || estado === '') {
    return { clave: 'diagnostico', etiqueta: 'Diagnóstico' };
  }
  if (typeof estado === 'number') {
    return normalizarDesdeNumero(estado);
  }
  const s = String(estado).trim();
  if (s === '') {
    return { clave: 'diagnostico', etiqueta: 'Diagnóstico' };
  }
  if (/^\d+$/.test(s)) {
    return normalizarDesdeNumero(Number(s));
  }
  if (s === 'Implementación') {
    return { clave: 'ejecucion', etiqueta: 'Ejecución' };
  }
  const normalizado = s as EtiquetaEstadoPlanAPI;
  if (ETIQUETAS_API_VALIDAS.includes(normalizado)) {
    const entry = ESTADOS_PLAN_NEGOCIO[normalizado];
    return { clave: entry.clave, etiqueta: entry.etiqueta };
  }
  return { clave: 'default', etiqueta: 'Sin clasificar' };
}

/**
 * Id de estado efectivo para el filtro del selector (misma regla que badges: sin id → 1 Diagnóstico).
 * Clave `default` (sin clasificar) → null (no coincide con 1, 2 ni 3).
 */
export function idEstadoPlanNegocioEfectivoParaFiltro(
  plan: Parameters<typeof estadoPlanNegocioRawParaUi>[0]
): 1 | 2 | 3 | 4 | null {
  const raw = estadoPlanNegocioRawParaUi(plan);
  const { clave } = normalizarEstado(raw);
  if (clave === 'diagnostico') return ID_PN_DIAGNOSTICO;
  if (clave === 'diseno') return ID_PN_DISENO;
  if (clave === 'ejecucion') return ID_PN_EJECUCION;
  if (clave === 'cerrado') return ID_PN_CERRADO;
  return null;
}

/** Plan en fase Ejecución (id 3 o etiqueta equivalente): tabs del plan en solo lectura y flujo de reapertura. */
export function planNegocioEstaEnEjecucion(
  plan: Parameters<typeof estadoPlanNegocioRawParaUi>[0]
): boolean {
  return idEstadoPlanNegocioEfectivoParaFiltro(plan) === ID_PN_EJECUCION;
}

/** Plan en estado Cerrado (id 4 o etiqueta equivalente): tabs del plan en solo lectura. */
export function planNegocioEstaCerrado(
  plan: Parameters<typeof estadoPlanNegocioRawParaUi>[0]
): boolean {
  return idEstadoPlanNegocioEfectivoParaFiltro(plan) === ID_PN_CERRADO;
}

function coercerIdFiltroPlanNegocio(
  idFiltro: number | string | null | undefined
): number | null {
  if (idFiltro == null || idFiltro === '') return null;
  const n = typeof idFiltro === 'number' ? idFiltro : Number(idFiltro);
  return Number.isNaN(n) ? null : n;
}

/**
 * Alinea el listado con el selector: Diagnóstico (1) incluye `id_estado_plan_negocio` null (como en la API).
 * Acepta id numérico o string (p. ej. ion-select).
 */
export function planNegocioCoincideConFiltroEstado(
  plan: Parameters<typeof estadoPlanNegocioRawParaUi>[0],
  idFiltro: number | string | null | undefined
): boolean {
  const id = coercerIdFiltroPlanNegocio(idFiltro);
  if (id == null) return true;
  return idEstadoPlanNegocioEfectivoParaFiltro(plan) === id;
}

/**
 * Devuelve la clase CSS para el badge según el estado (id numérico, texto de API o null → Diagnóstico).
 */
export function getClaseEstadoPlanNegocio(
  estado: number | string | null | undefined
): string {
  const { clave } = normalizarEstado(estado);
  return 'estado-pn-' + clave.replace(/_/g, '-');
}

/**
 * Devuelve la etiqueta para mostrar según el estado (valor de API o null → Diagnóstico).
 */
export function getEtiquetaEstadoPlanNegocio(
  estado: number | string | null | undefined
): string {
  const { etiqueta } = normalizarEstado(estado);
  return etiqueta;
}

/**
 * Obtiene el color hexadecimal para una clase CSS de estado plan negocio.
 */
export function obtenerColorEstadoPlanNegocio(clase: string): string {
  const clave = MAPEO_CLASES_ESTADOS_PLAN_NEGOCIO[clase] ?? 'default';
  return COLORES_ESTADOS_PLAN_NEGOCIO[clave].hex;
}

/**
 * Obtiene el color RGBA para una clase CSS (opacidad 1 = color puro).
 */
export function obtenerColorEstadoPlanNegocioRGBA(
  clase: string,
  opacity: number = 1
): string {
  const clave = MAPEO_CLASES_ESTADOS_PLAN_NEGOCIO[clase] ?? 'default';
  return COLORES_ESTADOS_PLAN_NEGOCIO[clave].rgba(opacity);
}
